// src/hooks/useAdminContext.ts
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { USERS, ME } from '../graphql/queries';
import {
  UPDATE_USER_ROLE,
  GRANT_TEMPORARY_ACCESS,
  REVOKE_ACCESS,
} from '../graphql/mutations';

import { useUserBlocking } from './useUserBlocking'; // ✅ Импортируем хук

export const useAdminContext = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<'username' | 'accessExpiry' | 'activity'>('username');
  const [isSuperadminCollapsed, setIsSuperadminCollapsed] = useState(true);
  const [isAdminsCollapsed, setIsAdminsCollapsed] = useState(true);
  const [isUsersCollapsed, setIsUsersCollapsed] = useState(true);
  const [isActivityInfoModalOpen, setIsActivityInfoModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
  } | null>(null);
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    user: null as any,
  });

  const openHistoryModal = (user: any) => {
    setHistoryModal({ isOpen: true, user });
  };

  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, user: null });
  };

  const { data: usersData, refetch: refetchUsers } = useQuery(USERS); // ❌ убрали refetch — кэш сам обновится
  const { data: meData } = useQuery(ME);

  const [updateUserRole] = useMutation(UPDATE_USER_ROLE);
  const [grantTemporaryAccess] = useMutation(GRANT_TEMPORARY_ACCESS);
  const [revokeAccess] = useMutation(REVOKE_ACCESS);

  // Уведомления
  const showNotification = (type: 'success' | 'error' | 'info' | 'warning', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // ✅ Подключаем useUserBlocking
  const { blockUser, unblockUser } = useUserBlocking(showNotification);

  // ✅ Меняет роль: user ↔ admin
  const handleMakeAdmin = (email: string) => {
    updateUserRole({
      variables: { email, newRole: 'admin' },
    })
      .then(() => {
        showNotification('success', 'Пользователь назначен администратором');
      })
      .catch((error: any) => {
        showNotification('error', `Ошибка: ${error.message}`);
      });
  };

  const handleRevokeAccess = (email: string) => {
    updateUserRole({
      variables: { email, newRole: 'user' },
    })
      .then(() => {
        showNotification('success', 'Права администратора сняты');
      })
      .catch((error: any) => {
        showNotification('error', `Ошибка: ${error.message}`);
      });
  };

  const handleMakeSuperadmin = (email: string) => {
    updateUserRole({
      variables: { email, newRole: 'superadmin' },
    })
      .then(() => {
        showNotification('success', 'Пользователь стал суперадмином');
      })
      .catch((error: any) => {
        showNotification('error', `Ошибка: ${error.message}`);
      });
  };

  // ✅ Открывает временный доступ (независимо от роли)
  const [accessModal, setAccessModal] = useState({
    isOpen: false,
    user: null as any,
    expiryDate: '',
    reason: undefined as string | undefined,
  });

  const openAccessModal = (user: any) => {
    setAccessModal({
      isOpen: true,
      user,
      expiryDate: '',
      reason: undefined,
    });
  };

  const closeAccessModal = () => {
    setAccessModal({
      isOpen: false,
      user: null,
      expiryDate: '',
      reason: undefined,
    });
  };

  const handleGrantAccess = async () => {
    if (!accessModal.user || !accessModal.expiryDate || !accessModal.reason) return;
    const expiryDate = new Date(accessModal.expiryDate);
    const now = new Date();
    const durationInHours = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60));
    if (durationInHours <= 0) {
      showNotification('error', 'Выберите дату в будущем');
      return;
    }
    try {
      await grantTemporaryAccess({
        variables: {
          email: accessModal.user.email,
          durationInHours,
          reason: accessModal.reason,
        },
      });
      showNotification('success', 'Временный доступ предоставлен');
      closeAccessModal();
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      showNotification('error', `Ошибка: ${errorMessage}`);
    }
  };

  // ✅ Закрывает доступ (даже если пользователь admin)
  const handleRevokeAccessOnly = async (email: string) => {
    try {
      await revokeAccess({ variables: { email } });
      showNotification('success', 'Доступ пользователя закрыт');
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      showNotification('error', `Ошибка закрытия доступа: ${errorMessage}`);
    }
  };

  // === Фильтрация и сортировка ===
  const users = usersData?.users || [];
  const currentUserRole = meData?.me?.role;

  const sortedAndFilteredUsers = (() => {
    let filtered = users;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u: any) =>
          u.username.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          (u.company && u.company.toLowerCase().includes(term))
      );
    }

    // ✅ Создаём копию массива перед сортировкой
    return [...filtered].sort((a: any, b: any) => {
      // 1. Сортировка по алфавиту
      if (sortOption === 'username') {
        return a.username.localeCompare(b.username);
      }

      // 2. ✅ НОВОЕ: Сортировка по активности (от неактивных к активным)
      if (sortOption === 'activity') {
        const weights: Record<string, number> = {
          'спящий': 1,
          'выходной': 2,
          'периодический': 3,
          'стандарт': 4,
          'нон-стоп': 5,
          'неопределено': 6,
        };
        const weightA = weights[a.activityPattern] ?? 6;
        const weightB = weights[b.activityPattern] ?? 6;
        return weightA - weightB; 
      }

      // 3. Сортировка по сроку подписки (твоя старая логика)
      const now = Date.now();
      const getSortWeight = (user: any) => {
        if (user.isBlocked) return { priority: 4, date: Infinity };
        const expiry = user.accessExpiry ? new Date(user.accessExpiry).getTime() : null;
        if (!expiry) return { priority: 1, date: -1 };
        if (expiry <= now) return { priority: 2, date: expiry };
        return { priority: 3, date: expiry };
      };

      const weightA_access = getSortWeight(a);
      const weightB_access = getSortWeight(b);

      if (weightA_access.priority !== weightB_access.priority) {
        return weightA_access.priority - weightB_access.priority;
      }

      return weightA_access.date - weightB_access.date;
    });
  })();

  const superadmins = sortedAndFilteredUsers.filter((u: any) => u.role === 'superadmin');
  const admins = sortedAndFilteredUsers.filter((u: any) => u.role === 'admin');
  const regularUsers = sortedAndFilteredUsers.filter((u: any) => u.role === 'user');

  // === Поддержка ресайза таблицы ===
  const [listKey, setListKey] = useState(0);
  const resizeTimeout = useRef<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeout.current) {
        window.clearTimeout(resizeTimeout.current);
      }
      resizeTimeout.current = window.setTimeout(() => {
        setListKey(prev => prev + 1);
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout.current) {
        window.clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  return {
    // State
    searchTerm,
    sortOption,
    isSuperadminCollapsed,
    setIsSuperadminCollapsed,
    isAdminsCollapsed,
    setIsAdminsCollapsed,
    isUsersCollapsed,
    setIsUsersCollapsed,
    isCreateUserModalOpen,
    setIsCreateUserModalOpen,
    notification,
    accessModal,
    listKey,
    users,
    currentUserRole,

    // Data
    data: usersData,
    superadmins,
    admins,
    regularUsers,
    refetchUsers,

    // Setters
    setSearchTerm,
    setSortOption,
    setNotification,
    setAccessModal,

    // Actions
    showNotification,
    handleMakeAdmin,
    handleRevokeAccess,
    handleMakeSuperadmin,
    openAccessModal,
    closeAccessModal,
    handleGrantAccess,
    handleRevokeAccessOnly,
    blockUser, // ✅ из useUserBlocking
    unblockUser, // ✅ из useUserBlocking
    historyModal,
    openHistoryModal,
    closeHistoryModal,
    isActivityInfoModalOpen,
    setIsActivityInfoModalOpen,
  };
};